import type { LegalDoc } from "./types";
import gdprEn from "./gdpr.en";
import gdprFr from "./gdpr.fr";
import gdprEs from "./gdpr.es";
import gdprDe from "./gdpr.de";
import gdprIt from "./gdpr.it";
import gdprPt from "./gdpr.pt";
import gdprNl from "./gdpr.nl";
import gdprMs from "./gdpr.ms";
import gdprZh from "./gdpr.zh";
import biometricEn from "./biometric.en";
import gdprEnV10 from "./v1_0/gdpr.en";
import gdprFrV10 from "./v1_0/gdpr.fr";
import gdprEsV10 from "./v1_0/gdpr.es";
import gdprDeV10 from "./v1_0/gdpr.de";
import gdprItV10 from "./v1_0/gdpr.it";
import gdprPtV10 from "./v1_0/gdpr.pt";
import gdprNlV10 from "./v1_0/gdpr.nl";
import gdprMsV10 from "./v1_0/gdpr.ms";
import gdprZhV10 from "./v1_0/gdpr.zh";

// Locale → GDPR/RGPD document. English is the source of truth and the fallback
// for any locale not present here.
const GDPR_BY_LANG: Record<string, LegalDoc> = {
  en: gdprEn,
  fr: gdprFr,
  es: gdprEs,
  de: gdprDe,
  it: gdprIt,
  pt: gdprPt,
  nl: gdprNl,
  ms: gdprMs,
  zh: gdprZh,
};

// v1.0 (effective 1 September 2026) — what /privacy serves until v1.1 takes effect.
const GDPR_V10_BY_LANG: Record<string, LegalDoc> = {
  en: gdprEnV10,
  fr: gdprFrV10,
  es: gdprEsV10,
  de: gdprDeV10,
  it: gdprItV10,
  pt: gdprPtV10,
  nl: gdprNlV10,
  ms: gdprMsV10,
  zh: gdprZhV10,
};

/** v1.1 of the privacy policy (the face-data retention rule) takes effect, and is published, at
 * 2026-11-01T00:00:00Z. Before that /privacy serves v1.0 unchanged; the rule is announced by the
 * 20 October email and the /biometric-data-retention page it links to. */
export const GDPR_V11_EFFECTIVE_AT = Date.parse("2026-11-01T00:00:00Z");

/** Resolve the GDPR policy for a language tag (e.g. "fr", "fr-FR") in force at `now`, falling back
 * to English for any locale not yet translated. */
export const getGdprDoc = (lang: string | undefined, now: number = Date.now()): LegalDoc => {
  const base = (lang || "en").toLowerCase().split("-")[0];
  if (now < GDPR_V11_EFFECTIVE_AT) return GDPR_V10_BY_LANG[base] ?? gdprEnV10;
  return GDPR_BY_LANG[base] ?? gdprEn;
};

// Locale → biometric data retention policy. English only for now (the master and the fallback);
// add biometric.<lang>.ts files here as they are translated.
const BIOMETRIC_BY_LANG: Record<string, LegalDoc> = {
  en: biometricEn,
};

/** Resolve the biometric data retention policy for a language tag, English fallback. */
export const getBiometricDoc = (lang: string | undefined): LegalDoc => {
  const base = (lang || "en").toLowerCase().split("-")[0];
  return BIOMETRIC_BY_LANG[base] ?? biometricEn;
};

export { BIOMETRIC_POLICY_PATH } from "./biometric.en";
export type { LegalDoc } from "./types";
